import { useEffect, useState } from "react";
import axios from "axios";
import DynamicTable from "../Components/DynamicTable";
import { useUser } from "../UserContext";
import AddBA from "./AddBA";
import SlidingSidebar, {
  ButtonControls,
  TabConfig,
} from "../Components/SlidingSidebar";
import {
  Stack,
  Paper,
  Typography,
  TextField,
  Grid,
  Box,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

interface EditData extends StaffMember {
  avg_sales: number;
  avg_professionalism: number;
  avg_engagement: number;
  avg_expertise: number;
  updated_at?: string;
}

interface StaffMember {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  total_events: number;
  avg_overall: number;
  rate: number;
}

interface PerformanceMetric {
  label: string;
  value: number;
}

const MetricDisplay: React.FC<PerformanceMetric> = ({ label, value }) => (
  <Box sx={{ textAlign: "center", p: 1 }}>
    <Typography variant="h4" sx={{ color: "primary.main" }}>
      {typeof value === "number" ? value.toFixed(1) : "0.0"}
    </Typography>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
  </Box>
);

const Staff = () => {
  const [data, setData] = useState<StaffMember[]>([]);
  const { selectedAgencyID } = useUser(); // Use the useUser hook instead of useContext
  console.log("selectedAgencyID", selectedAgencyID);

  const columns = [
    { header: "First Name", dataIndex: "first_name", key: "first_name" },
    { header: "Last Name", dataIndex: "last_name", key: "last_name" },
    { header: "Email", dataIndex: "email", key: "email" },
    { header: "# Events", dataIndex: "total_events", key: "total_events" },
    { header: "Overall Score", dataIndex: "avg_overall", key: "avg_overall" },
    { header: "Rate", dataIndex: "rate", key: "rate" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/ambassadors/ambassadors`,
          {
            params: { agency_id: selectedAgencyID },
          }
        );
        const filteredData = response.data.map(
          ({
            user_id,
            first_name,
            last_name,
            email,
            rate,
            total_events,
            avg_overall,
            avg_sales,
            avg_professionalism,
            avg_engagement,
            avg_expertise,
          }: {
            user_id: number;
            first_name: string;
            last_name: string;
            email: string;
            rate: number;
            total_events: number;
            avg_overall: number;
            avg_sales: number;
            avg_professionalism: number;
            avg_engagement: number;
            avg_expertise: number;
          }) => ({
            user_id,
            first_name,
            last_name,
            email,
            rate,
            total_events,
            avg_overall,
            avg_sales,
            avg_professionalism,
            avg_engagement,
            avg_expertise,
          })
        );
        setData(filteredData);
        console.log("filteredData", filteredData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [selectedAgencyID]); // Update dependency to selectedAgencyID

  const [editingStaff, setEditingStaff] = useState<EditData | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleEdit = (staff: any) => {
    setEditingStaff({
      user_id: staff.user_id,
      first_name: staff.first_name,
      last_name: staff.last_name,
      email: staff.email,
      rate: staff.rate,
      total_events: staff.total_events,
      avg_overall: staff.avg_overall,
      avg_sales: staff.avg_sales,
      avg_professionalism: staff.avg_professionalism,
      avg_engagement: staff.avg_engagement,
      avg_expertise: staff.avg_expertise,
    });
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setEditingStaff(null);
  };

  const handleEditChange = (field: string, value: string | number) => {
    if (editingStaff) {
      setEditingStaff({ ...editingStaff, [field]: value });
    }
  };

  const handleSave = async () => {
    if (!editingStaff) return;

    try {
      const { data: response } = await axios.put(
        `https://api.promoraven.com/ambassadors/ambassador-basic/${editingStaff.user_id}`,
        editingStaff
      );

      // Update the staff list with the returned data
      setData(
        data.map((s) =>
          s.user_id === editingStaff.user_id ? { ...s, ...response.user } : s
        )
      );

      setSnackbarOpen(true);
      handleCloseSidebar();
    } catch (error: any) {
      console.error("Error updating staff member:", error);
      // You could add error handling here, such as showing an error message
      if (error.response?.data?.error === "Email already in use") {
        // Handle email already in use error
      }
    }
  };

  const handleDelete = async (userId: number) => {
    try {
      await axios.delete(
        `https://api.promoraven.com/ambassadors/ambassador/${userId}`
      );
      setData(data.filter((s) => s.user_id !== userId));
      handleCloseSidebar();
    } catch (error) {
      console.error("Error deleting staff member:", error);
    }
  };

  const BasicInfoTab = (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="First Name"
            size="small"
            value={editingStaff?.first_name || ""}
            onChange={(e) => handleEditChange("first_name", e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            size="small"
            value={editingStaff?.last_name || ""}
            onChange={(e) => handleEditChange("last_name", e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            size="small"
            value={editingStaff?.email || ""}
            onChange={(e) => handleEditChange("email", e.target.value)}
            fullWidth
          />
          <TextField
            label="Rate"
            type="number"
            size="small"
            value={editingStaff?.rate || ""}
            onChange={(e) => handleEditChange("rate", Number(e.target.value))}
            fullWidth
          />
        </Stack>
      </Paper>
    </Stack>
  );

  const PerformanceTab = (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MetricDisplay
                label="Total Events"
                value={editingStaff?.total_events || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <MetricDisplay
                label="Overall Score"
                value={editingStaff?.avg_overall || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <MetricDisplay
                label="Sales"
                value={editingStaff?.avg_sales || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <MetricDisplay
                label="Professionalism"
                value={editingStaff?.avg_professionalism || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <MetricDisplay
                label="Engagement"
                value={editingStaff?.avg_engagement || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <MetricDisplay
                label="Expertise"
                value={editingStaff?.avg_expertise || 0}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Stack>
  );

  const tabs: TabConfig[] = [
    {
      label: "Basic Info",
      component: BasicInfoTab,
    },
    {
      label: "Performance",
      component: PerformanceTab,
    },
  ];

  return (
    <div>
      <AddBA />
      <DynamicTable data={data} columns={columns} onRowClick={handleEdit} />
      <SlidingSidebar
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        width="500px"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {editingStaff && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
              {tabs[activeTab].component}
            </Box>
            <ButtonControls>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save Changes
              </Button>
              <Button
                onClick={() => handleDelete(editingStaff.user_id)}
                variant="outlined"
                color="error"
              >
                Delete User
              </Button>
              <Button onClick={handleCloseSidebar} variant="outlined">
                Cancel
              </Button>
            </ButtonControls>
          </Box>
        )}
      </SlidingSidebar>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes saved successfully
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Staff;
